import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CarCard } from "@/components/CarCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { Clock, ShieldCheck, Truck, Zap, MapPin, Calendar, Search, Star } from "lucide-react";

const PERKS = [
  { icon: <Clock size={22} />, title: "24/7 Concierge", desc: "Round-the-clock support whenever you need us." },
  { icon: <ShieldCheck size={22} />, title: "Zero Hidden Fees", desc: "Transparent pricing. What you see is what you pay." },
  { icon: <Truck size={22} />, title: "Doorstep Delivery", desc: "We bring the car to you, anywhere in the city." },
  { icon: <Zap size={22} />, title: "Instant Payment", desc: "Secure Stripe checkout in seconds." },
];

export default function Home() {
  const [cars, setCars] = useState([]);
  const [location, setLocation] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/cars").then((res) => setCars(res.data.slice(0, 3)));
  }, []);

  return (
    <div className="App bg-slate-50">
      <Navbar />

      {/* Hero */}
      <section className="relative bg-emerald-950 emerald-grain overflow-hidden" data-testid="hero">
        <img
          src="/hero-us.jpg"
          alt="Luxury Mercedes-Benz on an American highway"
          className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 w-[55%] h-[90%] object-cover object-left [mask-image:linear-gradient(to_right,transparent,black_35%)]"
        />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-20 pb-40 relative z-10">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-emerald-300 text-sm font-medium bg-emerald-500/10 border border-emerald-500/20 rounded-full px-4 py-1.5">
              <Star size={14} /> Rated 4.9/5 by 12,000+ drivers
            </span>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-none text-white">
              Drive something <span className="text-emerald-400">extraordinary</span> today.
            </h1>
            <p className="mt-6 text-lg text-emerald-100/80 leading-relaxed max-w-xl">
              Premium & exotic car rentals delivered to your door. Book by the day, pay securely, and hit the road in minutes.
            </p>
          </div>
        </div>

        {/* Search card */}
        <div className="absolute inset-x-0 bottom-0 translate-y-1/2 z-20 px-5 sm:px-8">
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-emerald-100 p-5 sm:p-6" data-testid="hero-search">
            <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-500" size={18} />
                <Input
                  data-testid="hero-search-input"
                  placeholder="Pickup in Boston — e.g. Downtown, Logan Airport, Mattapan"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="pl-10 h-12 border-slate-200"
                />
              </div>
              <Button
                data-testid="hero-search-button"
                onClick={() => navigate("/fleet")}
                className="h-12 px-8 bg-emerald-900 hover:bg-emerald-800 rounded-xl"
              >
                <Search size={18} className="mr-2" /> Browse Fleet
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Perks bento */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-8">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {PERKS.map((p, i) => (
            <div
              key={i}
              className="bg-white rounded-3xl p-7 border border-slate-200/60 hover:border-emerald-500/40 hover:shadow-lg transition-all duration-300"
              data-testid={`perk-${i}`}
            >
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
                {p.icon}
              </span>
              <h3 className="mt-4 font-display font-bold text-lg text-emerald-950">{p.title}</h3>
              <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Featured fleet */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-emerald-950">
              Featured vehicles
            </h2>
            <p className="mt-2 text-slate-500">Hand-picked machines ready for your next journey.</p>
          </div>
          <Button variant="outline" onClick={() => navigate("/fleet")} data-testid="view-all-cars" className="hidden sm:flex border-slate-200">
            View all
          </Button>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {cars.map((car, i) => (
            <CarCard key={car.id} car={car} index={i} />
          ))}
        </div>
      </section>

      {/* Boston showcase */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-8" data-testid="boston-showcase">
        <div className="relative rounded-[2rem] overflow-hidden shadow-2xl">
          <img
            src="/boston-benz.jpg"
            alt="A Mercedes-Benz driving through the Boston skyline at golden hour"
            className="w-full h-[320px] sm:h-[440px] lg:h-[520px] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/90 via-emerald-950/30 to-transparent" />
          <div className="absolute bottom-0 left-0 p-7 sm:p-12 max-w-2xl">
            <span className="inline-flex items-center gap-2 text-emerald-300 text-sm font-medium bg-emerald-500/15 border border-emerald-400/25 rounded-full px-4 py-1.5 backdrop-blur-sm">
              <MapPin size={14} /> Proudly serving Greater Boston
            </span>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight">
              Command the city. Own the drive.
            </h2>
            <p className="mt-3 text-emerald-100/85 text-sm sm:text-base leading-relaxed max-w-xl">
              From Downtown to Logan Airport, we deliver premium rides across the Boston area — pick your car, we bring it to you.
            </p>
            <Button onClick={() => navigate("/fleet")} data-testid="boston-cta" className="mt-6 h-12 px-7 bg-white text-emerald-950 hover:bg-emerald-50 rounded-full font-semibold">
              Explore the fleet
            </Button>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-emerald-950 emerald-grain py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white text-center">How it works</h2>
          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {[
              { n: "01", icon: <Search size={20} />, t: "Choose your ride", d: "Browse our curated fleet and pick the perfect vehicle." },
              { n: "02", icon: <Calendar size={20} />, t: "Set your dates", d: "Select pickup & return, add insurance, see instant pricing." },
              { n: "03", icon: <Zap size={20} />, t: "Pay & drive", d: "Secure Stripe checkout and we deliver to your door." },
            ].map((s, i) => (
              <div key={i} className="bg-emerald-900/40 rounded-3xl p-8 border border-emerald-500/20" data-testid={`step-${i}`}>
                <div className="flex items-center gap-3 text-emerald-400">
                  <span className="font-display font-extrabold text-3xl">{s.n}</span>
                  <span className="h-9 w-9 rounded-lg bg-emerald-500/20 flex items-center justify-center">{s.icon}</span>
                </div>
                <h3 className="mt-5 font-display font-bold text-xl text-white">{s.t}</h3>
                <p className="mt-2 text-emerald-100/70 text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
